import React, { useEffect, useMemo, useState } from "react";
import Seo from "../../Component/Seo";

const titleOptions = ["Mr", "Mrs", "Miss", "Ms"];
const relationshipOptions = ["Father", "Mother", "Sister", "Brother", "Others"];

const initialValues = {
  title: "",
  nin: "",
  phoneNumber: "",
  relationship: "",
  residentialAddress: "",
  occupation: "",
  employerName: "",
  officeTelephone: "",
  officeEmail: "",
  bankStatement: null,
  guarantorSignature: null,
  acceptedTerms: false,
};

async function parseApiResponse(response) {
  const responseText = await response.text().catch(() => "");
  let payload = null;

  try {
    payload = responseText ? JSON.parse(responseText) : null;
  } catch (error) {
    payload = null;
  }

  if (!response.ok || String(payload?.status) !== "2000") {
    if (response.status === 413) {
      throw new Error("One or more uploaded files are too large. Please upload smaller files and try again.");
    }

    if (response.status === 401 || response.status === 403) {
      throw new Error("This form could not be submitted right now. Please try again later.");
    }

    throw new Error(payload?.description || "Something went wrong. Please try again later.");
  }

  return payload;
}

function getFriendlyErrorMessage(error, fallbackMessage) {
  const rawMessage = error?.message || "";

  if (rawMessage === "Failed to fetch" || rawMessage === "NetworkError when attempting to fetch resource.") {
    return fallbackMessage;
  }

  if (rawMessage.toLowerCase().includes("load failed")) {
    return fallbackMessage;
  }

  return rawMessage || fallbackMessage;
}

function validateFormValues(values) {
  const requiredFields = [
    values.title,
    values.nin,
    values.phoneNumber,
    values.relationship,
    values.residentialAddress,
    values.occupation,
  ];

  const hasMissingText = requiredFields.some((value) => !String(value || "").trim());

  if (hasMissingText || !values.bankStatement || !values.guarantorSignature || !values.acceptedTerms) {
    return "Please complete all required fields, accept the terms, and upload the required documents.";
  }

  if (!/^\d{11}$/.test(values.nin.trim())) {
    return "Please enter a valid 11-digit NIN.";
  }

  if (!/^\d{11}$/.test(values.phoneNumber.trim())) {
    return "Please enter a valid 11-digit phone number.";
  }

  if (values.officeTelephone.trim() && !/^\d{11}$/.test(values.officeTelephone.trim())) {
    return "Please enter a valid 11-digit office telephone number.";
  }

  return "";
}

function TextField({ label, name, type = "text", value, onChange, required = true, placeholder }) {
  return (
    <label className="block">
      <span className="mb-2 block text-[14px] font-semibold text-text-color1">
        {label}
        {required && <span className="text-[#A73838]"> *</span>}
      </span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="h-12 w-full rounded-[8px] border border-[#D9D5E8] bg-white px-4 text-[15px] text-text-color1 outline-none transition placeholder:text-[#8B849A] focus:border-primary-color focus:ring-4 focus:ring-[#8046F2]/10"
      />
    </label>
  );
}

function TextAreaField({ label, name, value, onChange, required = true, placeholder }) {
  return (
    <label className="block md:col-span-2">
      <span className="mb-2 block text-[14px] font-semibold text-text-color1">
        {label}
        {required && <span className="text-[#A73838]"> *</span>}
      </span>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        rows={4}
        placeholder={placeholder}
        className="w-full resize-none rounded-[8px] border border-[#D9D5E8] bg-white px-4 py-3 text-[15px] text-text-color1 outline-none transition placeholder:text-[#8B849A] focus:border-primary-color focus:ring-4 focus:ring-[#8046F2]/10"
      />
    </label>
  );
}

function SelectField({ label, name, value, onChange, options, required = true }) {
  return (
    <label className="block">
      <span className="mb-2 block text-[14px] font-semibold text-text-color1">
        {label}
        {required && <span className="text-[#A73838]"> *</span>}
      </span>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="h-12 w-full rounded-[8px] border border-[#D9D5E8] bg-white px-4 text-[15px] text-text-color1 outline-none transition focus:border-primary-color focus:ring-4 focus:ring-[#8046F2]/10"
      >
        <option value="">Select {label.toLowerCase()}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function FileField({ label, name, onChange, required = true, accept }) {
  return (
    <label className="block">
      <span className="mb-2 block text-[14px] font-semibold text-text-color1">
        {label}
        {required && <span className="text-[#A73838]"> *</span>}
      </span>
      <input
        type="file"
        name={name}
        onChange={onChange}
        required={required}
        accept={accept}
        className="block h-12 w-full cursor-pointer rounded-[8px] border border-[#D9D5E8] bg-white text-[14px] text-text-color1 outline-none file:mr-4 file:h-12 file:cursor-pointer file:border-0 file:bg-primary-color5 file:px-4 file:text-[14px] file:font-semibold file:text-primary-color focus:border-primary-color focus:ring-4 focus:ring-[#8046F2]/10"
      />
    </label>
  );
}

function StatusMessage({ type, message }) {
  const styles =
    type === "success"
      ? "border-[#A7D7B8] bg-[#F2FBF5] text-[#1F6B3A]"
      : type === "error"
      ? "border-[#E5B5B5] bg-[#FFF5F5] text-[#9D2C2C]"
      : "border-[#BFA2F8] bg-[#F5F1FE] text-text-color1";

  return (
    <p className={`rounded-[8px] border px-4 py-3 text-[14px] font-semibold ${styles}`}>
      {message}
    </p>
  );
}

function SkeletonBlock({ className = "" }) {
  return <div className={`animate-pulse rounded-[8px] bg-[#E9E3F7] ${className}`} />;
}

function FormSkeleton() {
  return (
    <div className="space-y-8" aria-hidden="true">
      <section className="rounded-[8px] border border-[#E7E1F5] bg-white p-5 shadow-sm sm:p-8">
        <SkeletonBlock className="mb-3 h-4 w-36" />
        <SkeletonBlock className="mb-4 h-8 w-3/4 max-w-[420px]" />
        <SkeletonBlock className="h-4 w-full max-w-[560px]" />
      </section>

      {[1, 2, 3].map((section) => (
        <section
          key={section}
          className="rounded-[8px] border border-[#E7E1F5] bg-white p-5 shadow-sm sm:p-8"
        >
          <div className="mb-6 border-b border-[#E7E1F5] pb-5">
            <SkeletonBlock className="mb-3 h-4 w-28" />
            <SkeletonBlock className="mb-4 h-8 w-64 max-w-full" />
            <SkeletonBlock className="h-4 w-full max-w-[620px]" />
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {Array.from({ length: section === 2 ? 5 : 4 }).map((_, index) => (
              <div key={index}>
                <SkeletonBlock className="mb-2 h-4 w-32" />
                <SkeletonBlock className={section === 2 && index === 0 ? "h-28" : "h-12"} />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function SectionHeader({ number, title, description }) {
  return (
    <div className="mb-6 border-b border-[#E7E1F5] pb-5">
      <p className="mb-2 text-[13px] font-bold uppercase tracking-[0.12em] text-primary-color">
        Section {number} of 3
      </p>
      <h2 className="text-[24px] text-text-color1 sm:text-[32px]">{title}</h2>
      {description && <p className="mt-3 max-w-3xl text-[15px] leading-7 text-[#5C556C]">{description}</p>}
    </div>
  );
}

function GuarantorForm() {
  const token = useMemo(() => new URLSearchParams(window.location.search).get("token"), []);
  const [values, setValues] = useState(initialValues);
  const [validation, setValidation] = useState({
    loading: true,
    error: "",
    data: null,
  });
  const [submitState, setSubmitState] = useState({
    loading: false,
    error: "",
    success: "",
  });

  useEffect(() => {
    let isMounted = true;

    async function validateFormToken() {
      if (!token) {
        setValidation({
          loading: false,
          error: "This guarantor form link is missing a validation token.",
          data: null,
        });
        return;
      }

      try {
        const response = await fetch(
          `/asset/guarantor/validate?token=${encodeURIComponent(token)}`
        );
        const payload = await parseApiResponse(response);

        if (isMounted) {
          setValidation({
            loading: false,
            error: "",
            data: payload.data,
          });
        }
      } catch (error) {
        console.error("Guarantor form validation failed", error);
        if (isMounted) {
          setValidation({
            loading: false,
            error: getFriendlyErrorMessage(
              error,
              "We could not verify this form right now. Please try again later."
            ),
            data: null,
          });
        }
      }
    }

    validateFormToken();

    return () => {
      isMounted = false;
    };
  }, [token]);

  const handleChange = (event) => {
    const { name, value, type, checked, files } = event.target;
    setValues((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : type === "file" ? files[0] : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!token) {
      setSubmitState({
        loading: false,
        error: "This guarantor form link is missing a validation token.",
        success: "",
      });
      return;
    }

    setSubmitState({
      loading: true,
      error: "",
      success: "",
    });

    const validationError = validateFormValues(values);

    if (validationError) {
      setSubmitState({
        loading: false,
        error: validationError,
        success: "",
      });
      return;
    }

    const payload = {
      title: values.title.trim(),
      nin: values.nin.trim(),
      phoneNumber: values.phoneNumber.trim(),
      relationship: values.relationship.trim(),
      residentialAddress: values.residentialAddress.trim(),
      occupation: values.occupation.trim(),
      termsAccepted: values.acceptedTerms,
    };
    const optionalPayload = {
      employerName: values.employerName.trim(),
      officeTelephone: values.officeTelephone.trim(),
      officeEmail: values.officeEmail.trim(),
    };
    const requestPayload = {
      ...payload,
      ...Object.fromEntries(
        Object.entries(optionalPayload).filter(([, value]) => Boolean(value))
      ),
    };

    const formData = new FormData();
    Object.entries(requestPayload).forEach(([key, value]) => {
      formData.append(key, String(value));
    });
    formData.append("data", JSON.stringify(requestPayload));
    formData.append("bankStatementFile", values.bankStatement);
    formData.append("signatureFile", values.guarantorSignature);

    try {
      const response = await fetch(
        `/asset/guarantor/submit?token=${encodeURIComponent(token)}`,
        {
          method: "POST",
          body: formData,
        }
      );
      const responsePayload = await parseApiResponse(response);

      setSubmitState({
        loading: false,
        error: "",
        success: responsePayload.description || "Form submitted successfully.",
      });
      setValues(initialValues);
      event.target.reset();
    } catch (error) {
      console.error("Guarantor form submission failed", error);
      setSubmitState({
        loading: false,
        error: getFriendlyErrorMessage(
          error,
          "We could not submit the form right now. Please try again later."
        ),
        success: "",
      });
    }
  };

  return (
    <main className="bg-[#FBFAFF] pt-[92px]">
      <Seo
        title="Asset Financing Guarantor Form | Halal Wealth"
        description="Public guarantor form for Halal Wealth Asset Finance applicants."
        pathname="/guarantor-form"
      />

      <section className="px-5 py-12 sm:px-10 lg:px-20">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.35fr]">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <p className="mb-4 inline-flex rounded-full bg-white px-4 py-2 text-[13px] font-bold text-primary-color shadow-sm">
              Asset Finance
            </p>
            <h1 className="text-[36px] leading-tight text-text-color1 sm:text-[52px]">
              Become a Guarantor
            </h1>
            <div className="mt-6 space-y-4 text-[16px] leading-8 text-[#5C556C]">
              <p>
                Halal Wealth is building a smarter, Shariah-compliant way for people to own what
                they need without interest, hidden charges, or compromising their values.
              </p>
              <p>
                As part of our Asset Finance program, participants can acquire essential items,
                from work tools to household needs, and repay in flexible, interest-free
                installments.
              </p>
              <p>
                By becoming a guarantor, you are helping someone you trust grow responsibly while
                supporting an ethical, transparent, and interest-free financial system.
              </p>
            </div>
            <div className="mt-8 rounded-[8px] border border-[#E7E1F5] bg-white p-5">
              <p className="mb-3 text-[14px] font-bold text-text-color1">Your role is simple:</p>
              <ul className="space-y-3 text-[14px] leading-6 text-[#5C556C]">
                <li>Confirm your relationship with the applicant.</li>
                <li>Assure us of their credibility and commitment.</li>
                <li>Provide your details for verification.</li>
              </ul>
            </div>
          </div>

          {validation.loading && (
            <FormSkeleton />
          )}

          {!validation.loading && validation.error && (
            <div className="rounded-[8px] border border-[#E7E1F5] bg-white p-8 shadow-sm">
              <StatusMessage type="error" message={validation.error} />
            </div>
          )}

          {!validation.loading && !validation.error && (
            <form onSubmit={handleSubmit} className="space-y-8" noValidate>
              {validation.data && (
                <section className="rounded-[8px] border border-[#E7E1F5] bg-white p-5 shadow-sm sm:p-8">
                  <p className="mb-2 text-[13px] font-bold uppercase tracking-[0.12em] text-primary-color">
                    Validated Request
                  </p>
                  <h2 className="text-[24px] text-text-color1 sm:text-[30px]">
                    Guarantor details for {validation.data.requestingUser}
                  </h2>
                  <p className="mt-3 text-[15px] leading-7 text-[#5C556C]">
                    This secure form is linked to{" "}
                    <span className="font-semibold text-text-color1">
                      {validation.data.guarantorEmail}
                    </span>
                    .
                  </p>
                </section>
              )}

            <section className="rounded-[8px] border border-[#E7E1F5] bg-white p-5 shadow-sm sm:p-8">
              <SectionHeader
                number="1"
                title="Personal Information"
                description="These details help us verify your identity and relationship with the applicant."
              />
              <div className="grid gap-5 md:grid-cols-2">
                <SelectField label="Title" name="title" value={values.title} onChange={handleChange} options={titleOptions} />
                <TextField label="NIN" name="nin" value={values.nin} onChange={handleChange} placeholder="Enter NIN" />
                <TextField label="Phone Number" name="phoneNumber" type="tel" value={values.phoneNumber} onChange={handleChange} placeholder="Enter phone number" />
                <SelectField label="Relationship" name="relationship" value={values.relationship} onChange={handleChange} options={relationshipOptions} />
              </div>
            </section>

            <section className="rounded-[8px] border border-[#E7E1F5] bg-white p-5 shadow-sm sm:p-8">
              <SectionHeader
                number="2"
                title="Address Information"
                description="Provide your residential and work information for verification."
              />
              <div className="grid gap-5 md:grid-cols-2">
                <TextAreaField label="Residential Address" name="residentialAddress" value={values.residentialAddress} onChange={handleChange} placeholder="Enter residential address" />
                <TextField label="Occupation" name="occupation" value={values.occupation} onChange={handleChange} placeholder="Enter occupation" />
                <TextField label="Employer's Name" name="employerName" value={values.employerName} onChange={handleChange} required={false} placeholder="Enter employer's name" />
                <TextField label="Office Telephone" name="officeTelephone" type="tel" value={values.officeTelephone} onChange={handleChange} required={false} placeholder="Enter office telephone" />
                <TextField label="Office Email" name="officeEmail" type="email" value={values.officeEmail} onChange={handleChange} required={false} placeholder="Enter office email" />
              </div>
            </section>

            <section className="rounded-[8px] border border-[#E7E1F5] bg-white p-5 shadow-sm sm:p-8">
              <SectionHeader
                number="3"
                title="Terms & Conditions"
                description="Review the guarantee terms and upload the supporting documents."
              />
              <div className="mb-6 rounded-[8px] bg-primary-color5 p-5">
                <ol className="list-decimal space-y-3 pl-5 text-[14px] leading-7 text-[#4E465F]">
                  <li>
                    In the event of default or absconding by the client, the guarantor shall
                    indemnify the company for all outstanding balances.
                  </li>
                  <li>
                    The guarantor cannot withdraw from this guarantee without the prior consent of
                    Halal Wealth, which may request a replacement guarantor as required.
                  </li>
                  <li>
                    This guarantee shall be continuous until the liability is fully repaid.
                  </li>
                </ol>
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                <FileField label="Upload your 3 months bank statement" name="bankStatement" onChange={handleChange} accept=".pdf,.doc,.docx,image/*" />
                <FileField label="Guarantor's Signature" name="guarantorSignature" onChange={handleChange} accept="image/*,.pdf" />
              </div>
              <label className="mt-6 flex cursor-pointer gap-3 text-[14px] leading-6 text-[#5C556C]">
                <input
                  type="checkbox"
                  name="acceptedTerms"
                  checked={values.acceptedTerms}
                  onChange={handleChange}
                  required
                  className="mt-1 h-4 w-4 cursor-pointer rounded border-[#D9D5E8] accent-primary-color"
                />
                <span>I confirm that the information provided is accurate and I accept the guarantor terms.</span>
              </label>

              {submitState.error && (
                <div className="mt-5">
                  <StatusMessage type="error" message={submitState.error} />
                </div>
              )}

              {submitState.success && (
                <div className="mt-5">
                  <StatusMessage type="success" message={submitState.success} />
                </div>
              )}

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                {/* <p className="text-[13px] text-[#7A7288]">Form submission will be connected once the endpoint is available.</p> */}
                <button
                  type="submit"
                  disabled={submitState.loading}
                  className="h-12 rounded-[8px] bg-primary-color px-8 text-[15px] font-bold text-white transition hover:bg-[#6C35DA] focus:outline-none focus:ring-4 focus:ring-[#8046F2]/20 disabled:cursor-not-allowed disabled:bg-[#BFA2F8]"
                >
                  {submitState.loading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </section>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

export default GuarantorForm;
